import { MapPin, Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@/components/ui';
import { useReplaceRmPincodes, useRmPincodes } from '@/hooks/useRmPincodes';
import type { RmPincodeEntry } from '@/services/rmPincode.service';

const TH =
  'whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-5 py-3.5 text-sm';

// Splits on commas, whitespace, or newlines so admins can paste a bulk list
// from a spreadsheet column in one go.
const parsePincodes = (raw: string): string[] => {
  const candidates = raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return [...new Set(candidates)];
};

const EditPincodesDialog = ({
  rm,
  open,
  onOpenChange,
}: {
  rm: RmPincodeEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { toast } = useToast();
  const replacePincodes = useReplaceRmPincodes();
  const [text, setText] = useState('');
  const invalid = useMemo(() => parsePincodes(text).filter((p) => !/^\d{4,10}$/.test(p)), [text]);

  useEffect(() => {
    // Defensive: dialog should never open without a target RM.
    if (!rm && open) onOpenChange(false);
  }, [rm, open, onOpenChange]);

  const handleOpenChange = (next: boolean): void => {
    if (next && rm) setText(rm.pincodes.join('\n'));
    onOpenChange(next);
  };

  const handleSave = (): void => {
    if (!rm) return;
    const pincodes = parsePincodes(text);
    if (invalid.length > 0) {
      toast.error(`Invalid pincode(s): ${invalid.slice(0, 5).join(', ')}`);
      return;
    }
    replacePincodes.mutate(
      { userId: rm.userId, pincodes },
      {
        onSuccess: () => {
          toast.success(`Updated ${pincodes.length} pincode(s) for ${rm.name}`);
          onOpenChange(false);
        },
        onError: () => toast.error('Could not update pincodes'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Pincode coverage - {rm?.name}</DialogTitle>
          <DialogDescription>
            One pincode per line (or comma-separated). Leads imported with a matching pincode route
            to this RM, or to whichever covering RM has the fewest leads/clients.
          </DialogDescription>
        </DialogHeader>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder={'400001\n400002\n411001'}
          className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm font-mono shadow-sm placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
        <p className="text-xs text-ink/50">
          {parsePincodes(text).length} pincode(s)
          {invalid.length > 0 && ` · ${invalid.length} invalid`}
        </p>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-line bg-white px-4 py-1.5 text-sm font-semibold text-ink hover:bg-cream"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={replacePincodes.isPending}
            className="rounded-md bg-brand px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50"
          >
            {replacePincodes.isPending ? 'Saving…' : 'Save'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminPincodesPage = () => {
  const { data: rows = [], isLoading, isError } = useRmPincodes();
  const [editing, setEditing] = useState<RmPincodeEntry | null>(null);

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
        Pincode routing
      </h1>
      <p className="mt-1.5 text-sm text-ink/60">
        Assign pincodes to each Sales RM. Excel-imported leads are auto-routed to a matching RM
        (least-loaded first); leads added manually always stay with the RM who added them.
      </p>

      <div className="mt-6 overflow-hidden rounded-xl border border-line bg-white shadow-sm">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-line bg-cream/60">
              <th className={TH}>Sales RM</th>
              <th className={TH}>Mobile</th>
              <th className={TH}>Pincodes covered</th>
              <th className={TH}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center text-sm text-ink/50">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center text-sm text-danger">
                  Failed to load Sales RMs. Please try again.
                </td>
              </tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center text-sm text-ink/50">
                  No Sales RM accounts yet.
                </td>
              </tr>
            )}
            {rows.map((rm) => (
              <tr key={rm.userId} className="transition-colors hover:bg-cream/50">
                <td className={`${TD} font-semibold text-ink`}>{rm.name}</td>
                <td className={`${TD} text-ink/60`}>{rm.mobile}</td>
                <td className={TD}>
                  {rm.pincodes.length === 0 ? (
                    <span className="text-ink/40">None assigned</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-ink/70">
                      <MapPin className="h-3.5 w-3.5" />
                      {rm.pincodes.length} pincode(s)
                    </span>
                  )}
                </td>
                <td className={TD}>
                  <button
                    type="button"
                    onClick={() => setEditing(rm)}
                    className="flex items-center gap-1 font-semibold text-brand transition-colors hover:text-brand-hover"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EditPincodesDialog
        rm={editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </div>
  );
};

export default AdminPincodesPage;
